import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { Search, Refresh, Facebook, Instagram } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pagesService, Page } from '../../services/pages.service';
import { adAccountsService, AdAccount } from '../../services/ad-accounts.service';
import { useUiStore } from '../../stores/ui.store';

export default function PagesPage() {
  const queryClient = useQueryClient();
  const { selectedAdAccountId, setSelectedAdAccountId } = useUiStore();
  const [search, setSearch] = useState('');

  // Fetch ad accounts
  const { data: accountsResult, isLoading: isLoadingAccounts } = useQuery({
    queryKey: ['adAccounts'],
    queryFn: () => adAccountsService.query(),
  });

  const adAccounts: AdAccount[] = accountsResult?.data || [];

  // Auto-select first account if none selected
  useEffect(() => {
    if (!selectedAdAccountId && adAccounts.length > 0) {
      setSelectedAdAccountId(adAccounts[0].id);
    }
  }, [adAccounts, selectedAdAccountId, setSelectedAdAccountId]);

  // Fetch pages (pages belong to tenant, not ad account - ad account is only for sync token)
  const { data: pagesResult, isLoading: isLoadingPages, error } = useQuery({
    queryKey: ['pages', search],
    queryFn: () => pagesService.query({
      search: search || undefined,
    }),
  });

  const pages: Page[] = pagesResult?.data || [];

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: () => pagesService.syncFromFacebook(selectedAdAccountId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  const formatNumber = (value?: number) =>
    value ? new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value) : '-';

  if (isLoadingAccounts) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (adAccounts.length === 0) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Facebook Pages
        </Typography>
        <Alert severity="warning">
          No ad accounts connected. Please connect an ad account first to view pages.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Facebook Pages
        </Typography>
        <Button
          variant="contained"
          startIcon={syncMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending || !selectedAdAccountId}
        >
          {syncMutation.isPending ? 'Syncing...' : 'Sync from Facebook'}
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {adAccounts.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Ad Account</InputLabel>
                <Select
                  value={selectedAdAccountId || ''}
                  onChange={(e) => setSelectedAdAccountId(e.target.value)}
                  label="Ad Account"
                >
                  {adAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              size="small"
              placeholder="Search pages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
          </Box>
        </CardContent>
      </Card>

      {syncMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Successfully synced pages from Facebook
        </Alert>
      )}

      {syncMutation.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to sync pages. Please try again.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load pages. Please try again.
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Page</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Fans</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Instagram</TableCell>
                <TableCell>Last Synced</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingPages ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : pages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Facebook sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        No pages found. Click "Sync from Facebook" to import your pages.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pages.map((page) => (
                  <TableRow key={page.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={page.picture_url} alt={page.name}>
                          <Facebook />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {page.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {page.fb_page_id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {page.category || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(page.fan_count)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={page.is_published ? 'Published' : 'Unpublished'}
                        color={page.is_published ? 'success' : 'default'}
                        size="small"
                      />
                      {page.verification_status && (
                        <Chip
                          label={page.verification_status}
                          color="info"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {page.instagram_username ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Instagram fontSize="small" color="secondary" />
                          <Typography variant="body2">
                            @{page.instagram_username}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not connected
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {page.last_synced_at
                          ? new Date(page.last_synced_at).toLocaleString()
                          : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
